import type { PoStatus, PurchaseOrder } from '@/services/procurement'

export type KanbanColumnKey = 'DRAFT_GROUP' | 'SENT' | 'PARTIAL' | 'COMPLETED' | 'ARCHIVED'

export function poStatusDisplayLabel(status: PoStatus): string {
  const labels: Record<PoStatus, string> = {
    DRAFT: 'Nháp',
    PENDING_APPROVAL: 'Chờ duyệt',
    APPROVED: 'Đã duyệt',
    SENT: 'Đã gửi NCC',
    PARTIAL: 'Giao một phần',
    RECEIVED: 'Đã nhận đủ',
    CANCELLED: 'Đã hủy',
    SHORT: 'Thiếu hàng',
  }
  return labels[status]
}

export interface KanbanColumnDef {
  key: KanbanColumnKey
  title: string
  subtitle: string
}

/** Cột LƯU TRỮ: chỉ hiện khi bật "Hiện đã lưu trữ"; PO vào cột này bằng chuột phải "Lưu trữ", không kéo thả. */
export const ARCHIVED_KANBAN_COLUMN: KanbanColumnDef = { key: 'ARCHIVED', title: 'LƯU TRỮ', subtitle: 'PO đã lưu trữ, lọc lại khi cần' }

export const KANBAN_COLUMNS: KanbanColumnDef[] = [
  { key: 'DRAFT_GROUP', title: 'NHÁP', subtitle: 'Bao gồm Nháp, Chờ duyệt, Đã duyệt' },
  { key: 'SENT', title: 'ĐANG GIAO', subtitle: 'Đã gửi nhà cung cấp' },
  { key: 'PARTIAL', title: 'NHẬN 1 PHẦN', subtitle: 'Đang nhận hoặc thiếu hàng chờ xử lý' },
  { key: 'COMPLETED', title: 'HOÀN THÀNH', subtitle: 'Đã nhận đủ, đã hủy hoặc thiếu hàng đã xử lý' },
]

/**
 * Checks whether a line is fully closed:
 * - received_qty >= order_qty (received in full)
 * - OR (received_qty + short_qty >= order_qty AND short_resolution IN ('accept', 'cancel_rest'))
 */
export function isLineFullyClosed(l: {
  qty?: number
  order_qty?: number
  receivedQty?: number
  received_qty?: number
  shortQty?: number
  short_qty?: number
  shortResolution?: string | null
  short_resolution?: string | null
}): boolean {
  const res = l.short_resolution ?? l.shortResolution
  if (res === 'wait' || res === 'claim') {
    return false
  }

  const o = Number(l.order_qty ?? l.qty) || 0
  const r = Number(l.received_qty ?? l.receivedQty) || 0
  const s = Number(l.short_qty ?? l.shortQty) || 0

  if (o > 0 && r >= o) return true
  if (o > 0 && r + s >= o && (res === 'accept' || res === 'cancel_rest')) return true
  if (o === 0 && (res === 'accept' || res === 'cancel_rest')) return true
  return false
}

/**
 * Pure mapping from real PO status + line short_resolutions to 4 Kanban display columns.
 */
export function mapStatusToKanbanColumn(
  status: PoStatus,
  lines: Array<{
    qty?: number
    order_qty?: number
    receivedQty?: number
    received_qty?: number
    shortQty?: number
    short_qty?: number
    short_resolution?: string | null
    shortResolution?: string | null
  }>,
): KanbanColumnKey {
  if (status === 'DRAFT' || status === 'PENDING_APPROVAL' || status === 'APPROVED') {
    return 'DRAFT_GROUP'
  }
  if (status === 'SENT') {
    return 'SENT'
  }
  if (status === 'PARTIAL') {
    return 'PARTIAL'
  }
  if (status === 'SHORT') {
    const allClosed = lines.length > 0 && lines.every(isLineFullyClosed)
    if (allClosed) {
      return 'COMPLETED'
    }
    return 'PARTIAL'
  }
  if (status === 'RECEIVED' || status === 'CANCELLED') {
    return 'COMPLETED'
  }
  return 'DRAFT_GROUP'
}

/**
 * Returns the specific badge info for a card in the HOÀN THÀNH column.
 */
export function getCompletedStatusBadge(
  status: PoStatus,
  lines: Array<{
    qty?: number
    order_qty?: number
    receivedQty?: number
    received_qty?: number
    shortQty?: number
    short_qty?: number
    short_resolution?: string | null
    shortResolution?: string | null
  }>,
): { label: string; tone: 'success' | 'warning' | 'neutral' } {
  if (status === 'RECEIVED') {
    return { label: 'Đã nhận đủ', tone: 'success' }
  }
  if (status === 'CANCELLED') {
    return { label: 'Đã hủy', tone: 'neutral' }
  }
  if (status === 'SHORT') {
    const allClosed = lines.length > 0 && lines.every(isLineFullyClosed)
    if (allClosed) {
      return { label: 'Thiếu hàng — đã xử lý', tone: 'warning' }
    }
  }
  return { label: 'Hoàn thành', tone: 'success' }
}

export type PoDeliveryStatusCode =
  | 'CANCELLED'
  | 'DELIVERED_FULL'
  | 'PARTIAL_DELIVERED'
  | 'OVERDUE'
  | 'UPCOMING'
  | 'UNDELIVERED'

export interface PoDeliveryStatusInfo {
  code: PoDeliveryStatusCode
  label: string
  tone: 'success' | 'warning' | 'danger' | 'info' | 'neutral'
}

/**
 * Derives delivery status for a PO based on Section 7 formulas.
 * Supports both:
 * - deriveDeliveryStatus(po, today?)
 * - deriveDeliveryStatus(status, deliveryDate, lines, today?)
 */
export function deriveDeliveryStatus(
  statusOrPo:
    | PoStatus
    | {
        status: PoStatus
        deliveryDate?: string | null
        lines?: Array<{
          qty?: number
          order_qty?: number
          receivedQty?: number
          received_qty?: number
          shortQty?: number
          short_qty?: number
        }>
      },
  deliveryDateOrToday?: string | null | undefined,
  linesParam?: Array<{
    qty?: number
    order_qty?: number
    receivedQty?: number
    received_qty?: number
    shortQty?: number
    short_qty?: number
  }>,
  todayParam?: string,
): PoDeliveryStatusInfo {
  let status: PoStatus
  let deliveryDate: string | null | undefined
  let lines: Array<{
    qty?: number
    order_qty?: number
    receivedQty?: number
    received_qty?: number
    shortQty?: number
    short_qty?: number
  }>
  let today: string

  if (typeof statusOrPo === 'object' && statusOrPo !== null) {
    status = statusOrPo.status
    deliveryDate = statusOrPo.deliveryDate
    lines = statusOrPo.lines || []
    today = (deliveryDateOrToday as string) || new Date().toISOString().slice(0, 10)
  } else {
    status = statusOrPo
    deliveryDate = deliveryDateOrToday
    lines = linesParam || []
    today = todayParam || new Date().toISOString().slice(0, 10)
  }
  if (status === 'CANCELLED') {
    return { code: 'CANCELLED', label: 'Đã hủy', tone: 'neutral' }
  }

  let totalOrdered = 0
  let totalRemaining = 0
  let totalReceived = 0

  for (const l of lines) {
    const ordered = Number(l.order_qty ?? l.qty ?? 0)
    const received = Number(l.received_qty ?? l.receivedQty ?? 0)
    const short = Number(l.short_qty ?? l.shortQty ?? 0)
    totalOrdered += ordered
    totalRemaining += ordered - received - short
    totalReceived += received
  }

  if (status === 'RECEIVED') {
    return { code: 'DELIVERED_FULL', label: 'Đã nhận đủ', tone: 'success' }
  }

  if (lines.length > 0 && totalOrdered > 0 && totalRemaining <= 0) {
    return { code: 'DELIVERED_FULL', label: 'Đã nhận đủ', tone: 'success' }
  }
  if (totalReceived > 0 && totalRemaining > 0) {
    return { code: 'PARTIAL_DELIVERED', label: 'Giao một phần', tone: 'info' }
  }
  if (deliveryDate && totalRemaining > 0 && deliveryDate < today) {
    return { code: 'OVERDUE', label: 'Quá hạn', tone: 'danger' }
  }

  const d = new Date(today)
  d.setDate(d.getDate() + 3)
  const threeDaysLater = d.toISOString().slice(0, 10)

  if (deliveryDate && totalRemaining > 0 && deliveryDate <= threeDaysLater) {
    return { code: 'UPCOMING', label: 'Sắp giao', tone: 'warning' }
  }

  return { code: 'UNDELIVERED', label: 'Chưa giao', tone: 'neutral' }
}

export function formatVnNumber(val: number): string {
  return val.toLocaleString('vi-VN')
}

export interface ReceivedProgressDisplay {
  text: string
  tooltip: string
  ratio: number
}

/**
 * Computes Kanban Card received progress display according to Answer A2:
 * 1. Normalize UOM (trim + lowercase).
 * 2. If all lines have valid & same UOM -> "Received Qty / Ordered Qty UOM".
 * 3. If 2+ distinct UOMs or missing/invalid UOM -> "X / Y dòng nhận đủ".
 * 4. Tooltip: per-line breakdown.
 */
export function getReceivedProgressDisplay(po: PurchaseOrder): ReceivedProgressDisplay {
  const lines = po.lines || []
  if (!lines.length) {
    return { text: '0 / 0 dòng nhận đủ', tooltip: 'Không có dòng hàng', ratio: 0 }
  }

  // 1. Calculate per-line ratio
  let sumRatio = 0
  let fullReceivedCount = 0
  const tooltipLines: string[] = []

  for (let i = 0; i < lines.length; i++) {
    const l = lines[i]
    const ordered = l.qty || 0
    const received = l.receivedQty || 0
    const r = ordered > 0 ? Math.min(received / ordered, 1) : 0
    sumRatio += r
    if (ordered > 0 && received >= ordered) {
      fullReceivedCount++
    }
    tooltipLines.push(
      `Dòng ${i + 1} (${l.itemName || l.itemCode}): ${formatVnNumber(received)}/${formatVnNumber(ordered)} ${l.uom || ''}`,
    )
  }
  const avgRatio = sumRatio / lines.length

  // 2. Check UOMs
  const hasEmptyUom = lines.some((l) => !l.uom || !l.uom.trim())
  const normalizedUoms = lines.map((l) => (l.uom ? l.uom.trim().toLowerCase() : ''))
  const uniqueUoms = new Set(normalizedUoms)

  if (!hasEmptyUom && uniqueUoms.size === 1) {
    let totalOrdered = 0
    let totalReceived = 0
    for (const l of lines) {
      totalOrdered += l.qty || 0
      totalReceived += l.receivedQty || 0
    }
    const displayUom = lines[0].uom.trim()
    return {
      text: `${formatVnNumber(totalReceived)} / ${formatVnNumber(totalOrdered)} ${displayUom}`,
      tooltip: tooltipLines.join('\n'),
      ratio: avgRatio,
    }
  }

  // Fallback for 2+ distinct UOMs or missing UOM
  return {
    text: `${fullReceivedCount} / ${lines.length} dòng nhận đủ`,
    tooltip: tooltipLines.join('\n'),
    ratio: avgRatio,
  }
}

/**
 * Checks if dropping a card from sourceStatus to targetCol is allowed.
 * Free drag: allowed to drop to any different column, except CANCELLED POs.
 */
export function isValidDropTransition(
  sourceStatus: PoStatus,
  targetCol: KanbanColumnKey,
  sourceCol?: KanbanColumnKey,
): boolean {
  if (sourceStatus === 'CANCELLED') return false
  if (targetCol === 'ARCHIVED' || sourceCol === 'ARCHIVED') return false
  if (sourceCol && sourceCol === targetCol) return false
  return true
}

/**
 * Checks if a PO is draggable. Free drag across columns.
 * Only CANCELLED or archived POs are non-draggable.
 */
export function isPoDraggable(po: PurchaseOrder): boolean {
  if (po.status === 'CANCELLED') return false
  if (po.archivedAt) return false
  return true
}

/**
 * Checks if a PO is eligible for archiving:
 * Only allowed when PO is in HOÀN THÀNH group:
 * - RECEIVED
 * - CANCELLED
 * - SHORT with all lines fully closed
 */
export function isPoArchiveEligible(po: {
  status: PoStatus
  lines?: Array<{
    qty?: number
    order_qty?: number
    receivedQty?: number
    received_qty?: number
    shortQty?: number
    short_qty?: number
    short_resolution?: string | null
    shortResolution?: string | null
  }>
}): boolean {
  const lines = po.lines || []
  if (po.status === 'RECEIVED' || po.status === 'CANCELLED') {
    return true
  }
  if (po.status === 'SHORT' && lines.length > 0) {
    return lines.every(isLineFullyClosed)
  }
  return false
}

/**
 * Computes remaining quantity for unclosed lines in a PO:
 * remaining = order_qty - received_qty (full unreceived qty, since short is overwritten upon close)
 */
export function computePoRemaining(lines: Array<{
  id: string
  itemName?: string
  itemCode?: string
  spec?: string
  spec_attributes?: Record<string, unknown> | null
  materialId?: string
  material_id?: string
  qty: number
  receivedQty: number
  shortQty?: number
  short_qty?: number
  shortResolution?: string | null
  short_resolution?: string | null
  uom: string
}>): Array<{
  id: string
  itemName: string
  itemCode: string
  spec?: string
  spec_attributes?: Record<string, unknown> | null
  materialId?: string
  qty: number
  receivedQty: number
  remainingQty: number
  uom: string
}> {
  return lines
    .filter((l) => !isLineFullyClosed(l))
    .map((l) => {
      const remaining = Math.max(0, l.qty - l.receivedQty)
      return {
        id: l.id,
        itemName: l.itemName || l.itemCode || 'Vật tư',
        itemCode: l.itemCode || '',
        materialId: l.materialId || l.material_id,
        ...(l.spec !== undefined ? { spec: l.spec } : {}),
        ...(l.spec_attributes !== undefined ? { spec_attributes: l.spec_attributes } : {}),
        qty: l.qty,
        receivedQty: l.receivedQty,
        remainingQty: remaining,
        uom: l.uom,
      }
    })
    .filter((l) => l.remainingQty > 0)
}
